import React, { useState } from "react"
import { Tag, Tooltip } from "antd"
import {
	SwapOutlined,
	DragOutlined,
	WarningOutlined,
	LinkOutlined,
} from "@ant-design/icons"
import { useDrag } from "react-dnd"
import Strings from "utils/strings"
import { getStatusColor, getStatusLabel } from "utils/status"
import { RouteAddress } from "../../types"
import { ChangeType } from "./types"
import {
	DND_TYPE,
	GROUPED_COLOR,
	MOVED_COLOR,
	REPOSITIONED_COLOR,
	TOOLTIP_COLOR,
	formatClock,
} from "./utils"

const Pill: React.FC<{
	addr: RouteAddress
	left: number
	eta?: number
	isOverridden: boolean
	color: string
	routeId: string
	changeType: ChangeType
	sequence?: number
	outOfFormatWarning?: boolean
	selected?: boolean
	onSelect?: () => void
	compact?: boolean
}> = ({
	addr,
	left,
	eta,
	isOverridden,
	color,
	routeId,
	changeType,
	sequence,
	outOfFormatWarning,
	selected,
	onSelect,
	compact,
}) => {
	const [{ isDragging }, drag] = useDrag({
		type: DND_TYPE,
		item: { orderId: addr.order.orderId, fromRouteId: routeId },
		collect: (monitor) => ({ isDragging: monitor.isDragging() }),
	})

	// HTML5 drag doesn't fire a clean mouseleave, so an uncontrolled Tooltip
	// would get stuck open over the drop target for the whole drag.
	const [tooltipOpen, setTooltipOpen] = useState(false)

	const order = addr.order
	const bundledOrderIds = order.bundledOrders?.map((o) => o.orderId) ?? []
	const isGrouped = bundledOrderIds.length > 0
	const isChanged = changeType !== "none"
	const changeBadgeColor =
		changeType === "moved" ? MOVED_COLOR : REPOSITIONED_COLOR
	const hasRing = isChanged || isGrouped
	const ringColor = isChanged ? changeBadgeColor : GROUPED_COLOR
	const hasIndicator = isGrouped || !!outOfFormatWarning || isChanged

	const tooltipContent = (
		<div style={{ minWidth: 240, maxWidth: 280 }}>
			<div style={{ fontWeight: 700, fontSize: 13.5, marginBottom: 6 }}>
				({sequence != null ? sequence : "-"}) {order.orderId}
			</div>
			{order.user?.name && (
				<div style={{ fontSize: 12.5, marginBottom: 2 }}>{order.user.name}</div>
			)}
			{order.user?.phone && (
				<div style={{ fontSize: 12.5, marginBottom: 2 }}>
					{order.user.phone}
				</div>
			)}
			{addr.address && (
				<div style={{ fontSize: 12, color: "#9aa0a8", marginBottom: 6 }}>
					{addr.address}
				</div>
			)}
			<div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 8 }}>
				{isOverridden
					? `≈ ${formatClock(eta)}`
					: `${formatClock(addr.eta)} - ${formatClock(addr.etd)}`}
			</div>
			{order.status && (
				<Tag color={getStatusColor(order.status)} style={{ marginBottom: 4 }}>
					{getStatusLabel(order.status)}
				</Tag>
			)}
			{outOfFormatWarning && (
				<div style={{ marginTop: 4, color: "#faad14" }}>
					<WarningOutlined style={{ marginRight: 4 }} />
					{Strings.routes.moveOrdersOutOfFormatWarning}
				</div>
			)}
			{isGrouped && (
				<>
					<div
						style={{
							display: "flex",
							alignItems: "center",
							gap: 6,
							fontSize: 11.5,
							fontWeight: 650,
							color: "#2dd4bf",
							marginTop: 8,
							marginBottom: 6,
						}}
					>
						<LinkOutlined />
						{Strings.formatString(
							Strings.routes.moveOrdersGroupedWithCount,
							bundledOrderIds.length
						)}
					</div>
					<div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
						{bundledOrderIds.map((id) => (
							<span
								key={id}
								style={{
									alignSelf: "flex-start",
									boxSizing: "border-box",
									maxWidth: "100%",
									fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
									fontSize: 11.5,
									background: "rgba(255,255,255,0.08)",
									border: "1px solid rgba(255,255,255,0.12)",
									borderRadius: 5,
									padding: "3px 8px",
									color: "#d7f5ef",
								}}
							>
								{id}
							</span>
						))}
					</div>
				</>
			)}
		</div>
	)

	return (
		<Tooltip
			title={tooltipContent}
			color={TOOLTIP_COLOR}
			visible={!isDragging && tooltipOpen}
			onVisibleChange={setTooltipOpen}
		>
			<div
				ref={drag}
				onClick={onSelect}
				style={{
					position: "absolute",
					left: `${left}%`,
					top: "50%",
					transform: `translate(-50%, -50%) scale(${selected ? 1.12 : 1})`,
					display: "flex",
					alignItems: "center",
					gap: compact ? 0 : 6,
					height: 30,
					boxSizing: "border-box",
					background: color,
					borderRadius: 8,
					padding: compact ? "0 4px" : "0 10px 0 4px",
					fontSize: 12.5,
					fontWeight: 650,
					cursor: "grab",
					opacity: isDragging ? 0.3 : 1,
					whiteSpace: "nowrap",
					boxShadow:
						(hasRing
							? `0 0 0 2px #fff, 0 0 0 3.5px ${ringColor}`
							: "0 1px 3px rgba(0,0,0,0.18)") +
						(selected ? ", 0 0 0 6px rgba(26,29,36,0.28)" : ""),
					zIndex: isDragging ? 3 : selected ? 3 : hasIndicator ? 2 : 1,
					transition: "transform 0.1s ease, padding 0.1s ease",
				}}
			>
				{sequence != null && (
					<span
						style={{
							display: "inline-flex",
							alignItems: "center",
							justifyContent: "center",
							minWidth: 18,
							height: 18,
							padding: "0 4px",
							borderRadius: 5,
							fontSize: 10.5,
							fontWeight: 700,
							fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
							background: "rgba(255,255,255,0.28)",
							color: "#fff",
							flexShrink: 0,
						}}
					>
						{sequence}
					</span>
				)}
				{!compact && (
					<span
						style={{
							fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
							color: "#fff",
							letterSpacing: "-0.02em",
							fontVariantNumeric: "tabular-nums",
							fontSize: 11.5,
							maxWidth: 92,
							overflow: "hidden",
							textOverflow: "ellipsis",
						}}
					>
						{order.orderId}
					</span>
				)}
				{outOfFormatWarning && (
					<span
						style={{
							position: "absolute",
							top: -7,
							left: -7,
							width: 15,
							height: 15,
							borderRadius: "50%",
							background: "#faad14",
							boxShadow: "0 0 0 1.5px #fff",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						<WarningOutlined style={{ color: "#fff", fontSize: 8 }} />
					</span>
				)}
				{/* Top-right corner: whichever matters more when both apply — a
					change in sequence/route (icon) over just being grouped (+N). */}
				{isChanged ? (
					<span
						style={{
							position: "absolute",
							top: -7,
							right: -7,
							width: 15,
							height: 15,
							borderRadius: "50%",
							background: changeBadgeColor,
							boxShadow: "0 0 0 1.5px #fff",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						{changeType === "moved" ? (
							<SwapOutlined style={{ color: "#fff", fontSize: 8 }} />
						) : (
							<DragOutlined style={{ color: "#fff", fontSize: 8 }} />
						)}
					</span>
				) : (
					isGrouped && (
						<span
							style={{
								position: "absolute",
								top: -8,
								right: -8,
								minWidth: 17,
								height: 17,
								padding: "0 4px",
								borderRadius: 9,
								background: GROUPED_COLOR,
								boxShadow: "0 0 0 1.5px #fff",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								fontSize: 10,
								fontWeight: 700,
								color: "#fff",
								fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
							}}
						>
							+{bundledOrderIds.length}
						</span>
					)
				)}
				{/* Rare case: a stop that's both changed and grouped — the change
					badge already took the top-right corner, so this falls back to
					the bottom-left one rather than disappearing entirely. */}
				{isChanged && isGrouped && (
					<span
						style={{
							position: "absolute",
							bottom: -8,
							left: -8,
							minWidth: 17,
							height: 17,
							padding: "0 4px",
							borderRadius: 9,
							background: GROUPED_COLOR,
							boxShadow: "0 0 0 1.5px #fff",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							fontSize: 10,
							fontWeight: 700,
							color: "#fff",
							fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
						}}
					>
						+{bundledOrderIds.length}
					</span>
				)}
			</div>
		</Tooltip>
	)
}

export default Pill
